import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TouchableInputProps } from '.';
import { hideModal, showModal } from '../../../actions/runtime';
import { analyticsTrackEvent } from '../../../core/analytics';
import {
  ACTION_CLICK,
  ACTION_FIRST_DELETE,
  UNIVERSAL_AUTOSUGGEST_INPUT,
} from '../../../core/analytics/events';
import { apiGetJson } from '../../../core/dapi';
import { getSymptomsIsEmergencyUrl } from '../../../core/dapi/symptom';
import logger from '../../../core/logger';
import { isMobile } from '../../../core/util/device';
import { isEmptyString } from '../../../core/util/string';
import { SolvReduxState } from '../../../reducers';
import { REQUEST_UNIVERSAL_SEARCH } from '../../../sagas/universalSearch';
import EmergencyModal from '../../EmergencyModal';
import { EMERGENCY_MODAL_ID } from '../../EmergencyModal/EmergencyModal';
import { AutoCompleteResult } from '../AutocompleteResults';
import { generateDefaultSuggestions } from '../Inputs/defaults';
import UserChoice from '../UserChoice';
import { FieldDisplayProps } from './inputs/Field';
import TextInput from './inputs/TextInput';
import dataTestIds from '../../../../src/test/test-ids';
import { HomepageSearchType } from '../../../routes/home/Homepage';
import { useFetchSuggestions } from '~/components/UniversalSearch/Homepage/hooks/useFetchSuggestions';
import { useHandleBlur } from '~/components/UniversalSearch/Homepage/hooks/useHandleBlur';
import { isAStructuredRfv } from '../util';

type Props = { tab: HomepageSearchType } & TouchableInputProps & Omit<FieldDisplayProps, 'label'>;

/**
 * Homepage input for the universal search.
 */
const UniversalInput = ({ touched, tab, ...props }: Props) => {
  const [suggestions, setSuggestions] = useState<Array<AutoCompleteResult>>([]);

  const { initialValue } = useSelector((state: SolvReduxState) => ({
    initialValue: state.universalSearch.displayQuery,
  }));
  const [value, setValue] = useState(initialValue ?? '');

  const dispatch = useDispatch();
  const toggleEmergencyModal = useCallback(
    (show: boolean) => {
      dispatch((show ? showModal : hideModal)(EMERGENCY_MODAL_ID));
    },
    [dispatch]
  );

  const conditionallyShowEmergencyToolTip = useCallback(
    (query: any) => {
      if (isEmptyString(query)) {
        return;
      }
      let withoutComma = query;
      if (withoutComma.includes(',')) {
        withoutComma = query.split(',').join(' ');
      }

      apiGetJson(getSymptomsIsEmergencyUrl(withoutComma))
        .then((response: any) => response.data.is_emergency && toggleEmergencyModal(true))
        .catch((err: any) => logger.debug(err));
    },
    [toggleEmergencyModal]
  );

  const fetchSuggestions = useFetchSuggestions();

  const isFirstDeleteRef = useRef(true);
  const setSelectedDocument = useCallback(
    (doc: any) => {
      setValue(doc.name);
      isFirstDeleteRef.current = true;
      conditionallyShowEmergencyToolTip(doc.name);
      dispatch({
        type: REQUEST_UNIVERSAL_SEARCH,
        fullDoc: doc,
        onlySelectDoc: true,
      });
    },
    [conditionallyShowEmergencyToolTip, dispatch]
  );

  useEffect(() => {
    if (!value || value?.trim() === '') {
      setSuggestions([]);
    } else {
      fetchSuggestions(value, false, setSelectedDocument, setSuggestions);
    }
  }, [fetchSuggestions, setSelectedDocument, value]);

  const handleBlur = useHandleBlur(value);

  const { universalSearch } = useSelector((state: SolvReduxState) => ({
    universalSearch: state.universalSearch,
  }));

  const handleInputChange = (val: string, e?: ChangeEvent<HTMLInputElement>) => {
    const nativeEvent = e?.nativeEvent as InputEvent;

    if (nativeEvent?.inputType === 'deleteContentBackward') {
      if (isFirstDeleteRef.current) {
        analyticsTrackEvent(UNIVERSAL_AUTOSUGGEST_INPUT, {
          action: ACTION_FIRST_DELETE,
          query: universalSearch.query,
          isMobile: isMobile(),
          isQueryAStructuredRfv: isAStructuredRfv(universalSearch.query, universalSearch.results),
        });
        isFirstDeleteRef.current = false;
      }
    } else {
      isFirstDeleteRef.current = true;
    }

    setValue(val);
  };

  // Experiment: Structured RFV
  const defaultSuggestions = generateDefaultSuggestions(setSelectedDocument);
  const LIMIT_SUGGESTIONS = 8;
  const displayedSuggestions =
    suggestions.length && value.length > 0
      ? suggestions.slice(0, LIMIT_SUGGESTIONS)
      : defaultSuggestions;
  return (
    <>
      <TextInput
        dataTestId={dataTestIds.homePage.symptomSearch}
        hideDivider
        label="What?"
        onBlur={handleBlur}
        onChange={handleInputChange}
        onClick={() => {
          analyticsTrackEvent(UNIVERSAL_AUTOSUGGEST_INPUT, {
            action: ACTION_CLICK,
            isMobile: isMobile(),
          });
        }}
        placeholder="Symptom or service"
        results={displayedSuggestions}
        selectAllOnFocus
        setIsTouched={touched}
        trackingLabel={UNIVERSAL_AUTOSUGGEST_INPUT}
        value={value}
        {...props}
      />
      <UserChoice results={displayedSuggestions} />
      <EmergencyModal />
    </>
  );
};

export default UniversalInput;
